import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { showToast } from "components/ToastContainer/index";
import {
  selectEditCreator,
  selectUpdateCreator
} from "pages/Creators/EditCreator/slice/selector";
import { useEditCreatorSlice } from "pages/Creators/EditCreator/slice/index";
import { useDropdownSlice } from "store/dropdownOptions/slice/index";
import {
  selectCities,
  selectCompanies,
  selectStates,
  selectPrimaryCategories,
  selectSubCategories,
  selectLanguages,
  selectTalentAgencyManagers
} from "store/dropdownOptions/slice/selector";

// ─── Constants ───────────────────────────────────────────────────────────────

const BOOLEAN_FIELDS = [
  "is_talent_manager_verified",
  "is_personal_manager_verified",
  "is_self_managed_verified"
];

const ID_FIELDS = [
  "city_id",
  "state_id",
  "talent_company_id",
  "talent_manager_id"
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const normalizeCreatorData = (raw) =>
  Object.entries(raw).reduce((acc, [key, value]) => {
    if (key === "barter_preferences") {
      acc[key] = Array.isArray(value)
        ? [...new Set(value.map((v) => v.toLowerCase().replace(/\s+/g, "_")))]
        : [];
      return acc;
    }

    if (BOOLEAN_FIELDS.includes(key)) {
      acc[key] = value === 1 || value === true || value === "Yes";
      return acc;
    }

    acc[key] = value ?? "";
    return acc;
  }, {});

const sanitizePayload = (payload) =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    acc[key] = ID_FIELDS.includes(key) && value === "" ? null : value;
    return acc;
  }, {});

const computeChangedFields = (form, snapshot) =>
  Object.keys(form).reduce((acc, key) => {
    const current = form[key];
    const original = snapshot[key];

    if (Array.isArray(current) || Array.isArray(original)) {
      const curr = Array.isArray(current) ? [...current].sort() : [];
      const orig = Array.isArray(original) ? [...original].sort() : [];
      if (JSON.stringify(curr) !== JSON.stringify(orig)) acc[key] = current;
      return acc;
    }

    if (current !== original) acc[key] = current;
    return acc;
  }, {});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useEditCreatorForm = () => {
  const dispatch = useDispatch();
  const { actions: editCreatorActions } = useEditCreatorSlice();
  const { actions: dropdownActions } = useDropdownSlice();
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Selectors ──────────────────────────────────────────────────────────────
  const { data, loading } = useSelector(selectEditCreator);
  const { loading: updateLoading, success: updateSuccess } =
    useSelector(selectUpdateCreator);
  const { data: States, loading: stateLoading } = useSelector(selectStates);
  const { data: Cities, loading: citiesLoading } = useSelector(selectCities);
  const { data: Companies } = useSelector(selectCompanies);
  const { data: Languages, loading: languageLoading } =
    useSelector(selectLanguages);
  const { data: PrimaryCategories, loading: primaryCategoriesLoading } =
    useSelector(selectPrimaryCategories);
  const { data: SubCategories, loading: subCategoriesLoading } =
    useSelector(selectSubCategories);
  const { data: TalentAgencyManagers, loading: talentAgencyManagersLoading } =
    useSelector(selectTalentAgencyManagers);

  // ── State ──────────────────────────────────────────────────────────────────
  const [form, setForm] = useState({});
  const [emailError, setEmailError] = useState("");
  const [managerEmailError, setManagerEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [managerPhoneError, setManagerPhoneError] = useState("");
  const snapshotRef = useRef({});

  const updateSnapshot = useCallback((value) => {
    snapshotRef.current = value;
  }, []);

  // ── Initial data load ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    const normalized = { ...normalizeCreatorData(data), sub_categories: [] };
    setForm(normalized);
    updateSnapshot(normalized);
  }, [data]);

  // ── State → city fetch ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!data || !States?.length) return;

    const matchedState = States.find(
      (s) => s.name.toLowerCase() === data.state_name?.toLowerCase()
    );

    const merged = {
      ...normalizeCreatorData(data),
      sub_categories: [],
      state_id: matchedState?.id || "",
      city_id: "",
      city_name: data.city_name || ""
    };

    setForm(merged);
    updateSnapshot(merged);

    if (matchedState?.id) {
      dispatch(dropdownActions.getCitiesRequest({ state_id: matchedState.id }));
    }
  }, [data, States]);

  // ── City match → city_id ───────────────────────────────────────────────────
  useEffect(() => {
    if (!Cities?.length || !data?.city_name) return;

    const matchedCity = Cities.find(
      (c) => c.name.toLowerCase() === data.city_name.toLowerCase()
    );

    if (matchedCity) {
      setForm((prev) => ({ ...prev, city_id: matchedCity.id }));
      updateSnapshot({ ...snapshotRef.current, city_id: matchedCity.id });
    }
  }, [Cities]);

  // ── SubCategories ready → populate ────────────────────────────────────────
  const subCategoriesReady = useMemo(() => {
    if (!data?.sub_categories?.length || !SubCategories?.length) return false;
    return data.sub_categories.every((id) =>
      SubCategories.some((s) => s.id === id)
    );
  }, [SubCategories, data]);

  useEffect(() => {
    if (!subCategoriesReady) return;
    setForm((prev) => ({ ...prev, sub_categories: data.sub_categories }));
    updateSnapshot({
      ...snapshotRef.current,
      sub_categories: data.sub_categories
    });
  }, [subCategoriesReady]);

  // ── Redirect on save success ───────────────────────────────────────────────
  useEffect(() => {
    if (!updateSuccess) return;
    showToast("Creator updated successfully", "success");
    dispatch(editCreatorActions.clearCreatorState());
    navigate(`/creators${location.search}`);
  }, [updateSuccess]);

  // ── Bootstrap dispatches ───────────────────────────────────────────────────
  useEffect(() => {
    dispatch(dropdownActions.getStatesRequest());
    dispatch(dropdownActions.getLanguagesRequest());
    dispatch(
      dropdownActions.getCompaniesRequest({ type: "talent_management" })
    );
    dispatch(dropdownActions.getPrimaryCategoriesRequest());
  }, []);

  useEffect(() => {
    if (creatorId) dispatch(editCreatorActions.getCreatorById({ creatorId }));
  }, [creatorId]);

  useEffect(() => {
    if (!data?.primary_categories?.length) return;
    dispatch(
      dropdownActions.getSubCategoriesRequest({
        primaryCategoryIds: data.primary_categories
      })
    );
  }, [data]);

  useEffect(() => {
    if (!data?.talent_company_id) return;
    dispatch(
      dropdownActions.getTalentAgencyManagersRequest({
        talent_company_id: data.talent_company_id
      })
    );
  }, [data]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const changedFields = useMemo(
    () => computeChangedFields(form, snapshotRef.current),
    [form]
  );

  const hasChanges = Object.keys(changedFields).length > 0;

  // ── Field change handlers ──────────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    const { id, name, value, type, checked } = e.target;
    const fieldId = id || name;

    const phoneFields = ["phone", "personal_manager_phone"]; // ← add here
    if (phoneFields.includes(fieldId) && value.length > 10) return;

    setForm((prev) => ({
      ...prev,
      [fieldId]: type === "checkbox" ? checked : value
    }));
  }, []);

  const handlePrimaryCategoryChange = useCallback(
    (val) => {
      const filteredSubs = (form.sub_categories || []).filter((subId) =>
        SubCategories?.some(
          (sub) => sub.id === subId && val.includes(sub.primary_category_id)
        )
      );

      setForm((prev) => ({
        ...prev,
        primary_categories: val,
        sub_categories: filteredSubs
      }));

      const newlyAdded = val.filter(
        (id) => !(form.primary_categories || []).includes(id)
      );
      if (newlyAdded.length > 0) {
        dispatch(
          dropdownActions.getSubCategoriesRequest({
            primaryCategoryIds: newlyAdded
          })
        );
      }
    },
    [form.primary_categories, form.sub_categories, SubCategories]
  );

  const handleSubCategoryChange = useCallback(
    (val) => {
      const countPerPrimary = {};
      for (const subId of val) {
        const sub = SubCategories?.find((s) => s.id === subId);
        if (!sub) continue;
        countPerPrimary[sub.primary_category_id] =
          (countPerPrimary[sub.primary_category_id] || 0) + 1;
        if (countPerPrimary[sub.primary_category_id] > 2) return;
      }
      setForm((prev) => ({ ...prev, sub_categories: val }));
    },
    [SubCategories]
  );

  const handleStateChange = useCallback(
    (value) => {
      const matchedState = States?.find((s) => s.id === value);
      setForm((prev) => ({
        ...prev,
        state_id: value,
        state_name: matchedState?.name || "",
        state_short_name: matchedState?.short_name?.toUpperCase() || "",
        city_id: null,
        city_name: ""
      }));
      dispatch(dropdownActions.getCitiesRequest({ state_id: value }));
    },
    [States]
  );

  const handleCityChange = useCallback(
    (val) => {
      const matchedCity = Cities?.find((c) => c.id === val);
      setForm((prev) => ({
        ...prev,
        city_id: val,
        city_name: matchedCity?.name || ""
      }));
    },
    [Cities]
  );

  const handleTalentCompanyChange = useCallback(
    (value) => {
      const matchedCompany = Companies?.find((c) => c.id === value);
      setForm((prev) => ({
        ...prev,
        talent_company_id: value,
        talent_company_name: matchedCompany?.company_name || "",
        talent_agency_manager: ""
      }));
      if (value) {
        dispatch(
          dropdownActions.getTalentAgencyManagersRequest({
            talent_company_id: value
          })
        );
      }
    },
    [Companies]
  );

  const hasErrors = !!(
    phoneError ||
    emailError ||
    managerPhoneError ||
    managerEmailError
  );

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    if (!hasChanges || hasErrors) return;

    const payload = { ...changedFields };

    // Always send both category arrays together to prevent partial DB overwrites
    const categoryChanged =
      "primary_categories" in changedFields ||
      "sub_categories" in changedFields;

    if (categoryChanged) {
      payload.primary_categories = form.primary_categories;
      payload.sub_categories = form.sub_categories;
    }

    dispatch(
      editCreatorActions.updateCreator({
        id: creatorId,
        payload: sanitizePayload(payload)
      })
    );
  }, [hasChanges, changedFields, form, creatorId, hasErrors]);

  // ── Search handlers ────────────────────────────────────────────────────────
  const handlePrimaryCategorySearch = useCallback(
    (text) =>
      dispatch(dropdownActions.getPrimaryCategoriesRequest({ search: text })),
    []
  );

  const handleSubCategorySearch = useCallback(
    (text) =>
      dispatch(dropdownActions.getSubCategoriesRequest({ search: text })),
    []
  );

  const handleLanguageSearch = useCallback(
    (text) => dispatch(dropdownActions.getLanguagesRequest({ search: text })),
    []
  );

  return {
    // Data
    form,
    setForm,
    data,
    hasChanges,

    // Loading states
    loading,
    updateLoading,
    stateLoading,
    citiesLoading,
    languageLoading,
    primaryCategoriesLoading,
    subCategoriesLoading,
    talentAgencyManagersLoading,

    // Dropdown data
    States,
    Cities,
    Companies,
    Languages,
    PrimaryCategories,
    SubCategories,
    TalentAgencyManagers,

    // Handlers
    handleChange,
    handleSave,
    handleStateChange,
    handleCityChange,
    handleTalentCompanyChange,
    handlePrimaryCategoryChange,
    handleSubCategoryChange,
    handlePrimaryCategorySearch,
    handleSubCategorySearch,
    handleLanguageSearch,

    // Navigation
    navigate,
    location,

    phoneError,
    setPhoneError,
    emailError,
    setEmailError,
    managerPhoneError,
    setManagerPhoneError,
    managerEmailError,
    setManagerEmailError,
    hasErrors
  };
};
